import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Calculator, TrendingUp, Wallet, Rocket } from "lucide-react";
import { Button } from "./ui/button";

const eur = (n) => Math.round(n).toLocaleString("fr-FR");

export const ROICalculator = () => {
  const nav = useNavigate();
  const [ticket, setTicket] = useState(12);
  const [clients, setClients] = useState(40);
  const [days, setDays] = useState(5);
  const [uplift, setUplift] = useState(25);
  const PRO_COST = 9;

  const { monthlyRevenue, extra, net, roi } = useMemo(() => {
    const monthlyClients = clients * days * 4.33;
    const monthlyRevenue = monthlyClients * ticket;
    const extra = (monthlyRevenue * uplift) / 100;
    return { monthlyRevenue, extra, net: extra - PRO_COST, roi: extra > 0 ? Math.round(extra / PRO_COST) : 0 };
  }, [ticket, clients, days, uplift]);

  const Slider = ({ label, value, set, min, max, step, suffix }) => (
    <div>
      <div className="flex items-center justify-between">
        <label className="text-sm font-semibold text-urban-teal/80">{label}</label>
        <span className="font-display font-extrabold text-urban-teal">{value}{suffix}</span>
      </div>
      <input type="range" min={min} max={max} step={step} value={value} onChange={(e) => set(Number(e.target.value))}
        className="w-full mt-2 accent-street-gold" data-testid={`roi-${label.split(" ")[0].toLowerCase()}`} />
    </div>
  );

  return (
    <div id="simulateur" className="scroll-mt-24 bg-white rounded-3xl shadow-warm-lg overflow-hidden mt-16 grid lg:grid-cols-2" data-testid="roi-calculator">
      <div className="p-8 sm:p-10">
        <span className="inline-flex items-center gap-2 bg-street-gold/15 text-street-gold px-3 py-1 rounded-full text-sm font-bold"><Calculator size={15} /> Simulateur de gains</span>
        <h2 className="text-2xl sm:text-3xl font-display font-extrabold text-urban-teal mt-4">Combien StreatEat peut vous rapporter ?</h2>
        <p className="text-urban-teal/60 mt-2 text-sm">Ajustez vos chiffres, on estime le chiffre d'affaires que la visibilité Pro peut débloquer.</p>
        <div className="space-y-5 mt-6">
          <Slider label="Panier moyen" value={ticket} set={setTicket} min={5} max={40} step={1} suffix=" €" />
          <Slider label="Clients par jour" value={clients} set={setClients} min={5} max={200} step={5} suffix="" />
          <Slider label="Jours d'activité / semaine" value={days} set={setDays} min={1} max={7} step={1} suffix=" j" />
          <Slider label="Nouveaux clients grâce à la visibilité" value={uplift} set={setUplift} min={5} max={40} step={1} suffix=" %" />
        </div>
      </div>
      <div className="bg-urban-teal p-8 sm:p-10 text-white flex flex-col">
        <p className="text-white/60 text-sm">Votre CA mensuel actuel estimé</p>
        <p className="font-display font-extrabold text-2xl">{eur(monthlyRevenue)} €</p>
        <div className="grid grid-cols-2 gap-4 mt-6">
          <div className="bg-white/10 rounded-2xl p-4">
            <TrendingUp size={20} className="text-fresh-mint" />
            <p className="font-display font-extrabold text-2xl mt-1" data-testid="roi-extra">+{eur(extra)} €</p>
            <p className="text-white/60 text-xs">Revenus supplémentaires / mois</p>
          </div>
          <div className="bg-white/10 rounded-2xl p-4">
            <Wallet size={20} className="text-street-gold" />
            <p className="font-display font-extrabold text-2xl mt-1">{PRO_COST} €</p>
            <p className="text-white/60 text-xs">Coût de l'offre Pro / mois</p>
          </div>
        </div>
        <div className="bg-street-gold rounded-2xl p-5 mt-4">
          <p className="text-white/90 text-xs font-bold uppercase tracking-wide">Gain net estimé</p>
          <p className="font-display font-extrabold text-4xl" data-testid="roi-net">+{eur(net)} € <span className="text-lg font-bold">/ mois</span></p>
          <p className="text-white/90 text-sm mt-1 flex items-center gap-1"><Rocket size={15} /> Retour sur investissement : <span className="font-extrabold">x{roi}</span></p>
        </div>
        <Button onClick={() => nav("/connexion?tab=signup&plan=pro")} className="rounded-full bg-white text-urban-teal hover:bg-white/90 font-bold h-12 mt-5" data-testid="roi-cta">Passer en Pro et capter ces clients</Button>
        <p className="text-white/40 text-[11px] mt-3">Estimation indicative basée sur vos données. Résultats réels variables selon la zone et la saison.</p>
      </div>
    </div>
  );
};
